import { useEffect, useId, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { cn } from '@/lib/cn';

/**
 * Shared modal, safe to nest (Phase A).
 *
 * Inline creation stacks dialogs — Register Equipment → Add Equipment Type →
 * Add Category — so three behaviours are coordinated through one module-level
 * stack rather than per-instance state:
 *
 *  - **Escape closes only the top-most modal.** Every mounted modal used to
 *    register its own `document` listener, so one keypress collapsed the whole
 *    stack and discarded in-progress work. Each handler now returns unless its
 *    own id is on top.
 *  - **Background scroll stays locked until the last modal closes.** The lock
 *    was previously released by whichever modal unmounted first, so closing an
 *    inner dialog let the page scroll behind the ones still open. It is now
 *    refcounted.
 *  - **z-index increases with depth**, so a nested modal always paints above
 *    its parent regardless of DOM order.
 *
 * PP-1 (2026-09-08) adds the dialog accessibility the crop editor needs and
 * every other modal benefits from — all additive, defaults unchanged:
 *
 *  - **Initial focus** moves into the dialog on open: `initialFocusRef` if
 *    given, else the first focusable control in the body, else the panel.
 *    Focus already inside the panel (an `autoFocus` field) is left alone.
 *  - **Focus is trapped**: Tab / Shift+Tab cycle within the panel.
 *  - **Focus returns** to whatever had it before the dialog opened.
 *  - **`disableBackdropClose`** keeps a click outside from discarding work.
 *  - `aria-labelledby` is a unique generated id (was the same literal for
 *    every instance, so nested dialogs shared a label).
 *  - Below `sm` the panel is full-width and full-height; the rounded card
 *    returns from `sm:` up.
 */

/** Ids of currently-open modals, outermost first. Last entry is the top-most. */
const modalStack: symbol[] = [];
/** Refcount for the body scroll lock — released only when it reaches zero. */
let scrollLockCount = 0;

const BASE_Z_INDEX = 50;
const Z_INDEX_STEP = 10;

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  /** Element to focus on open; defaults to the first focusable control in the body. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /** When true a click on the backdrop does nothing (Escape and the Close button still work). */
  disableBackdropClose?: boolean;
  /** Overrides the generated `aria-labelledby` target. */
  labelledBy?: string;
}

function focusables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.hasAttribute('aria-hidden') && el.tabIndex >= 0,
  );
}

export function Modal({
  open,
  onClose,
  title,
  children,
  size = 'md',
  initialFocusRef,
  disableBackdropClose = false,
  labelledBy,
}: ModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const idRef = useRef<symbol | null>(null);
  if (idRef.current === null) idRef.current = Symbol('modal');
  const [depth, setDepth] = useState(0);
  const generatedTitleId = useId();
  const titleId = labelledBy ?? generatedTitleId;

  // Layout effect so depth (and therefore z-index) is applied before paint —
  // a nested modal must never flash underneath its parent.
  useLayoutEffect(() => {
    if (!open) return;
    const id = idRef.current!;
    modalStack.push(id);
    setDepth(modalStack.length - 1);

    scrollLockCount += 1;
    if (scrollLockCount === 1) document.body.style.overflow = 'hidden';

    return () => {
      const i = modalStack.indexOf(id);
      if (i !== -1) modalStack.splice(i, 1);

      scrollLockCount -= 1;
      if (scrollLockCount <= 0) {
        scrollLockCount = 0;
        document.body.style.overflow = '';
      }
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const id = idRef.current!;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Only the top-most modal reacts; parents below stay open.
      if (modalStack[modalStack.length - 1] !== id) return;
      onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Initial focus on open; focus return on close.
  useEffect(() => {
    if (!open) return;
    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const panel = panelRef.current;
    if (panel && !panel.contains(document.activeElement)) {
      const target =
        initialFocusRef?.current ??
        (bodyRef.current ? focusables(bodyRef.current)[0] : undefined) ??
        panel;
      target.focus({ preventScroll: true });
    }
    return () => {
      if (previouslyFocused && previouslyFocused.isConnected) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
    // initialFocusRef is a stable ref object; only `open` drives this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const sizeClass = {
    sm: 'max-w-sm',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    '2xl': 'max-w-6xl',
  }[size];

  const trapTab = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab' || !panelRef.current) return;
    const items = focusables(panelRef.current);
    if (items.length === 0) {
      e.preventDefault();
      panelRef.current.focus();
      return;
    }
    const first = items[0]!;
    const last = items[items.length - 1]!;
    const active = document.activeElement;
    if (e.shiftKey) {
      if (active === first || active === panelRef.current || !panelRef.current.contains(active)) {
        e.preventDefault();
        last.focus();
      }
    } else if (active === last || !panelRef.current.contains(active)) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 flex items-center justify-center p-0 sm:p-4"
      style={{ zIndex: BASE_Z_INDEX + depth * Z_INDEX_STEP }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onKeyDown={trapTab}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity motion-reduce:transition-none"
        onClick={disableBackdropClose ? undefined : onClose}
        aria-hidden="true"
        data-testid="modal-backdrop"
      />
      {/* Panel */}
      <div
        ref={panelRef}
        tabIndex={-1}
        className={cn(
          'relative z-10 w-full bg-card shadow-xl outline-none',
          'h-full max-h-[100dvh] overflow-y-auto rounded-none',
          'sm:h-auto sm:max-h-[90vh] sm:rounded-xl',
          sizeClass,
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 id={generatedTitleId} className="text-base font-semibold text-foreground">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-11 w-11 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground sm:h-8 sm:w-8"
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
          </button>
        </div>
        {/* Body */}
        <div ref={bodyRef} className="px-6 py-5">
          {children}
        </div>
      </div>
    </div>
  );
}
